import { enableProdMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';
import Lara from '@primeng/themes/lara';

// Override Lara's default emerald (green) primary with blue, matching the old saga-blue theme
const LaraBlue = definePreset(Lara, {
    semantic: {
        primary: {
            50:  '{blue.50}',
            100: '{blue.100}',
            200: '{blue.200}',
            300: '{blue.300}',
            400: '{blue.400}',
            500: '{blue.500}',
            600: '{blue.600}',
            700: '{blue.700}',
            800: '{blue.800}',
            900: '{blue.900}',
            950: '{blue.950}'
        }
    },
    components: {
        paginator: {
            root: {
                background: '#ebf7fc',
                borderRadius: '0'
            },
            navButton: {
                background: 'transparent',
                hoverBackground: 'transparent',
                selectedBackground: '#61C250',
                color: 'rgba(0,0,0,0.6)',
                hoverColor: 'rgba(0,0,0,0.87)',
                selectedColor: '#ffffff',
                borderRadius: '4px'
            }
        }
    }
});

// TranslateService factory for loading JSON translation files
export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { importProvidersFrom } from '@angular/core';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LocalStorageService, SessionStorageService } from 'angular-web-storage';
import { DatePipe, Location } from '@angular/common';
import { DateFormatPipe } from './app/shared/pipes/date-format.pipe';
import { StartWithPipe } from './app/shared/pipes/start-with.pipe';
import { EvenOddPipe } from './app/shared/pipes/even-odd.pipe';

import { environment } from './environments/environment';
import { APP_CONFIG_INITIALIZER_PROVIDER } from './app/core/config/app-config.initializer';
import { AppComponent } from './app/app.component';
import { APP_ROUTES } from './app/app.routes';

// Core interceptors
import { AuthInterceptor } from './app/core/interceptors/auth.interceptor';
import { ResponseInterceptor } from './app/core/interceptors/response.interceptor';
import { OverlayRequestResponseInterceptor } from './app/core/overlay/overlay-request-response.interceptor';

// Core services
import { DataService } from './app/core/services/data.service';
import { FilterService } from './app/core/services/filter.service';
import { SorterService } from './app/core/services/sorter.service';
import { TrackByService } from './app/core/services/trackby.service';
import { DialogService } from './app/core/services/dialog.service';
import { AuthService } from './app/core/services/auth.service';
import { EventBusService } from './app/core/services/event-bus.service';
import { UserPersistenceService } from './app/core/services/userPersistence.service';
import { GoogleService } from './app/core/services/google.service';

// Shared services
import { FormControlService } from './app/shared/services/form-control.service';
import { FormGroupGenerationService } from './app/shared/services/form-group-generation.service';
import { ModalService } from './app/shared/services/modal.service';
import { SharedDataService } from './app/shared/services/shared-data.service';
import { CommonDataService } from './app/project/services/commonData.service';
import { CommonToolDataService } from './app/project/services/commonToolData.service';
import { ProjectsService } from './app/project/services/project.service';
import { ToolDataService } from './app/project/services/toolData.service';
import { DocumentUploadDataService } from './app/project/services/documentUploadDataService';
import { ToolsHelperService } from './app/project/tools/tools-helper.service';
import { MyProjectGridService } from './app/project/my-projects/my-project-grid.service';
import { WorkflowActionService } from './app/project/services/workflowAction.service';
import { MoreInfoCommonService } from './app/project/more-info-page/more-info-service.component';
import { ShowImagePreviewService } from './app/project/show-image-preview/show-image-preview.service';
import { CertificateService } from './app/certificate/services/certificate.service';
import { ShowAllCertificateGridService } from './app/certificate/show-all-certificates/show-all-certificate-grid.service';

// Calculator services
import { CalculatorCommonService } from './app/project/calculators/calculatorCommonService';
import { gblCalculatorCommonService } from './app/project/calculators/gblCalculatorCommonService';
import { CalculatorDataService } from './app/project/services/calculatorData.service';

// Document comment services
import { ProjectDocumentCommentCommonService } from './app/project/documentComment/documentCommentService';
import { MeasureDocumentUploadDataService } from './app/project/services/measureDocumentUploadDataService';

// Login / user role services
import { UserRoleConfigService } from './app/login/user-role-configuration/userroleconfig.service';
import { ServiceReqService } from './app/login/service-request/servicerequest.service';
import { HpcRoleConfigService } from './app/login/hpc-role-configuration/hpcroleconfig.service';
import { HpcPartnerConfigurationService } from './app/login/hpc-partner-configuration/hpc-partner-configuration.service';
import { userRoleConfigurationService } from './app/login/user-role-configuration/user-role-configuration.service';
import { serviceRequestService } from './app/login/service-request/service-request.service';
import { hpcRoleConfigurationService } from './app/login/hpc-role-configuration/hpc-role-configuration.service';
import { HpcPartnerConfigurationGridService } from './app/login/hpc-partner-configuration/hpc-partner-configuration-grid.service';

// Dashboard services
import { DashboardService } from './app/dashboard/services/dashboard.service';
import { SelectAuditorReviewerService } from './app/dashboard/services/selectAuditorReviewer.service';
import { MasterTrackerGridService } from './app/dashboard/ca-master-tracker/master-tracker-grid.service';
import { MasterTrackerGridService as AdminMasterTrackerGridService } from './app/admin/master-tracker/master-tracker-grid.service';
import { MasterTrackerService } from './app/dashboard/services/master-tracker.service';
import { MasterTrackerService as AdminMasterTrackerService } from './app/admin/services/master-tracker.service';
import { CertificationTrackerPortfolioService } from './app/dashboard/services/certification-tracker-portfolio.service';
import { CertificationTrackerPortfolioGridService } from './app/dashboard/certification-tracker-portfolio/certification-tracker-portfolio-grid.service';
import { DashboardGridService } from './app/dashboard/dashboard-grid.service';
import { DecarbonizationPlanGridService } from './app/dashboard/decarbonization-plan/decarbonization-plan-grid.service';
import { DecarbonizationService } from './app/dashboard/services/decarbonization.service';
import { MasterTrackerPortfolioGridService } from './app/dashboard/master-tracker-portfolio/master-tracker-portfolio-grid.service';

// Admin services
import { AdminService } from './app/admin/services/admin.service';
import { CertificationTrackerGridService } from './app/admin/certification-tracker/certification-tracker-grid.service';
import { ExecutiveSummaryGridService } from './app/admin/executive-summary/executive-summary-grid.service';
import { ExecutiveSummaryService } from './app/admin/services/executive-summary.service';
import { AdminSettingsGridService } from './app/admin/admin-settings/admin-settings-grid.service';
import { HomeInspectorService } from './app/admin/services/home-inspector.service';
import { HomeInspectorDashboardGridService } from './app/admin/home-inspectors-dashboard/home-inspector-dashboard-grid.service';
import { downloadStatisticService } from './app/admin/download-statistics/download-statistics.service';
import { NotificationsCenterGridService } from './app/admin/notifications-center/notifications-center-grid.service';
import { CertificationTrackerService } from './app/admin/services/certification-tracker.service';
import { ProjectStudyAdminService } from './app/admin/project-study-admin/project-study-admin.component.service';

// Prometric services
import { EdgeExpertsService } from './app/prometric/services/edge-expert-service';
import { PrometricCommonService } from './app/prometric/services/prometricCommon.service';
import { PrometricService } from './app/prometric/services/prometric.service';
import { EdgeExpertsGridService } from './app/prometric/edge-experts/edge-experts-grid.service';
import { GenerateEligibilityService } from './app/prometric/generate-eligibility/generate-eligibility.service';
import { AddCandidatesGridService } from './app/prometric/add-candidates/add-candidates-grid.service';
import { EdgeExpertsDirectoryGridService } from './app/prometric/edge-experts-directory/edge-experts-directory-grid.service';
import { PrometricParticipationAgreementDataServiceService } from './app/prometric/services/prometricParticipationAgreementDataService.service';
import { EdgeFacultyGridService } from './app/prometric/edge-faculty/edge-faculty-grid.service';

// Certificate services
import { HomeInspectionCertificateService } from './app/home-inspection-certificate/services/homeinspection-certificate.service';
import { OperationalCertificateService } from './app/operational-certificate/services/operational-certificate.service';
import { OperationalShowAllCertificateGridService } from './app/operational-certificate/operational-show-all-certificates/operational-show-all-certificate-grid.service';
import { HomeInspectionCertificationService } from './app/project/home-Inspection-Certification/home-inspection-certification.service';

// Other services
import { OrganizationService } from './app/organization/organization.component.service';
import { ReleaseInfoService } from './app/release-info/release-info.service';
import { DatabaseAdminGridService } from './app/database-admin/database-admin-grid.service';
import { DatabasePutService } from './app/database-admin/services/database-put.service';
import { DatabasePostService } from './app/database-admin/services/database-post.service';
import { ProjectRegistrationService } from './app/project/project-registration/project-registration.service';
import { GrowlerService } from './app/core/growler/growler.service';

// Grid services
import { CreateBulkProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-projects-grid.service';
import { CreateBulkOfficeProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-office-projects-grid.service';
import { CreateBulkApartmentsProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-apartments-projects-grid.service';
import { CreateBulkRetailProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-retail-projects-grid.service';
import { CreateBulkResortProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-resort-projects-grid.service';
import { CreateBulkHotelProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-hotel-projects-grid.service ';
import { CreateBulkHealthcareProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-healthcare-projects-grid.service';
import { CreateBulkIndustrialProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-industrial-projects-grid.service';
import { CreateBulkEducationProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-education-projects-grid.service';
import { CreateBulkServicedApartmentProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-servicedapartment-projects-grid.service';
import { CreateBulkMixedProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-mixed-projects-grid.service ';
import { CreateBulkRetailV2ProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-retail-v2-projects-grid.service';
import { CreateBulkHospitalityV2ProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-hospitality-v2-projects-grid.service';
import { CreateBulkHomesV2ProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-homes-v2-projects-grid.service';
import { CreateBulkHospitalsV2ProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-hospitals-v2-projects-grid.service';
import { CreateBulkEducationV2ProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-education-v2-projects-grid.service';
import { CreateBulkOfficesV2ProjectsGridService } from './app/shared/widgets/create-bulk-projects/create-bulk-offices-v2-projects-grid.service';
import { TransitionHistoryGridService } from './app/shared/widgets/transition-history-table/transition-history-grid.service';

import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

if (environment.production) {
    enableProdMode();
}

function configurePdfViewerAssetsFolder() {
    const scriptSources = Array.from(document.querySelectorAll('script[src]'))
        .map(script => script.getAttribute('src') || '');
    const mainScriptSource = scriptSources.find(src => /(^|\/)main([-.].+)?\.js($|\?)/.test(src));

    if (!mainScriptSource) {
        pdfDefaultOptions.assetsFolder = 'assets';

        console.log('PDF viewer assets path', {
            mainScriptSource,
            assetsFolder: pdfDefaultOptions.assetsFolder
        });

        return;
    }

    const mainScriptUrl = new URL(mainScriptSource, window.location.origin);
    const assetsFolder = new URL('assets/', new URL('.', mainScriptUrl)).toString().replace(/\/$/, '');

    pdfDefaultOptions.assetsFolder = assetsFolder;

    console.log('PDF viewer assets path', {
        mainScriptSource,
        mainScriptUrl: mainScriptUrl.toString(),
        assetsFolder: pdfDefaultOptions.assetsFolder
    });
        
}

configurePdfViewerAssetsFolder();
bootstrapApplication(AppComponent, {
    providers: [
        APP_CONFIG_INITIALIZER_PROVIDER,
        provideRouter(APP_ROUTES, withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        provideNativeDateAdapter(),
        providePrimeNG({ theme: { preset: LaraBlue, options: { darkModeSelector: false } } }),
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        importProvidersFrom(
            NgbModule, 
            LoadingBarModule,
            NgxDaterangepickerMd.forRoot()
        ),
        

        // HTTP Interceptors (registered once globally)
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: OverlayRequestResponseInterceptor, multi: true },

        // Angular Material
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill', floatLabel: 'always', hideRequiredMarker: true } },
{
provide: OVERLAY_DEFAULT_CONFIG,
useValue: {
usePopover: false
}
},
        // Third-party
        DeviceDetectorService,
        LocalStorageService,
        SessionStorageService,
        DatePipe,
        DateFormatPipe,
        StartWithPipe,
        EvenOddPipe,
        Location,

        // Core services
        DataService, FilterService, SorterService, TrackByService, DialogService,
        AuthService, EventBusService, UserPersistenceService, GoogleService,

        // Shared services
        FormControlService, FormGroupGenerationService, ModalService, SharedDataService,
        GrowlerService,
        CreateBulkProjectsGridService, CreateBulkOfficeProjectsGridService,
        CreateBulkApartmentsProjectsGridService, CreateBulkRetailProjectsGridService,
        CreateBulkResortProjectsGridService, CreateBulkHotelProjectsGridService,
        CreateBulkHealthcareProjectsGridService, CreateBulkIndustrialProjectsGridService,
        CreateBulkEducationProjectsGridService, CreateBulkServicedApartmentProjectsGridService,
        CreateBulkMixedProjectsGridService, CreateBulkRetailV2ProjectsGridService,
        CreateBulkHospitalityV2ProjectsGridService, CreateBulkHomesV2ProjectsGridService,
        CreateBulkHospitalsV2ProjectsGridService, CreateBulkEducationV2ProjectsGridService,
        CreateBulkOfficesV2ProjectsGridService, TransitionHistoryGridService,

        // Project services
        CommonDataService, CommonToolDataService, ProjectsService, ToolDataService, DocumentUploadDataService,
        ToolsHelperService, MyProjectGridService, WorkflowActionService,
        MoreInfoCommonService, ShowImagePreviewService,
        ProjectRegistrationService, HomeInspectionCertificationService,

        // Calculator services
        CalculatorCommonService, gblCalculatorCommonService, CalculatorDataService,

        // Document comment services
        ProjectDocumentCommentCommonService, MeasureDocumentUploadDataService,

        // Certificate services
        CertificateService, ShowAllCertificateGridService,
        HomeInspectionCertificateService,
        OperationalCertificateService, OperationalShowAllCertificateGridService,

        // Login / user services
        UserRoleConfigService, ServiceReqService, HpcRoleConfigService, HpcPartnerConfigurationService,
        userRoleConfigurationService, serviceRequestService, hpcRoleConfigurationService, HpcPartnerConfigurationGridService,

        // Dashboard services
        DashboardService, SelectAuditorReviewerService, MasterTrackerGridService, MasterTrackerService,
        AdminMasterTrackerGridService, AdminMasterTrackerService,
        CertificationTrackerPortfolioService, CertificationTrackerPortfolioGridService,
        DashboardGridService, DecarbonizationPlanGridService, DecarbonizationService, MasterTrackerPortfolioGridService,

        // Admin services
        AdminService, CertificationTrackerGridService, ExecutiveSummaryGridService, ExecutiveSummaryService,
        AdminSettingsGridService, HomeInspectorService, HomeInspectorDashboardGridService,
        downloadStatisticService, NotificationsCenterGridService, CertificationTrackerService, ProjectStudyAdminService,

        // Prometric services
        EdgeExpertsService, PrometricCommonService, PrometricService,
        EdgeExpertsGridService, GenerateEligibilityService, AddCandidatesGridService,
        EdgeExpertsDirectoryGridService, PrometricParticipationAgreementDataServiceService, EdgeFacultyGridService,

        // Other feature services
        OrganizationService, ReleaseInfoService,
        DatabaseAdminGridService, DatabasePutService, DatabasePostService,
    ]
}).catch(err => console.error(err));

